//Componentes generales
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ProductList from '../../components/ProductList/ProductList';
import SelectList from '../../components/SelectList/SelectList';
import Spinner from '../../components/Spinner/Spinner';

//Context
import productsContext from '../../context/productos/productsContext';
import authContext from '../../context/authentication/authContex';

// Alertas
import Swal from 'sweetalert2';

//Estilos
import './Product.css';
import { VER_PRODUCTOS } from '../../utils/permisos';
import { REQUERIMIENTO } from '../../utils/roles';
import { CODATR_TIPO, CODATR_GRUPO, CODATR_SUBGRUPO, getOpcionesAtributo } from '../../utils/atributosProducto';

/* 
    Vista donde se gestiona toda la lógica de listado de productos.
        Donde props no se encuentra en uso.
*/
function Product(props) {
  //Context Auth
  const aContext = useContext(authContext);
  const { usuario } = aContext;

  //Context Add
  const pContext = useContext(productsContext);
  const { products, atributos, actualizarProducts, actualizarProductsDB, actualizarProductDBIndividual, actualizarAtributos } = pContext;

  const [loading, setLoading] = useState(true);
  const [productosConAtributos, setProductosConAtributos] = useState([]);

  //states de los filtros por atributo (Tipo/Grupo/Subgrupo). Cada uno es la opción {label, value: valAtr} seleccionada, o null.
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [filtroGrupo, setFiltroGrupo] = useState(null);
  const [filtroSubgrupo, setFiltroSubgrupo] = useState(null);

  const ocultarPrecio = usuario.userType.every(item => item.typeName === REQUERIMIENTO);

  /*
        Opciones de los 3 filtros por atributo, armadas a partir del listado completo de atributos
        (no solo los de los productos ya cargados), según su codAtr: 05TIPO, 10GRP y 15SUBG.
    */
  const opcionesTipo = useMemo(
    () => getOpcionesAtributo(atributos, CODATR_TIPO).map((o) => ({ label: `${o.valAtr} - ${o.descrpVal}`, value: o.valAtr })),
    [atributos]
  );
  const opcionesGrupo = useMemo(
    () => getOpcionesAtributo(atributos, CODATR_GRUPO).map((o) => ({ label: `${o.valAtr} - ${o.descrpVal}`, value: o.valAtr })),
    [atributos]
  );
  const opcionesSubgrupo = useMemo(
    () => getOpcionesAtributo(atributos, CODATR_SUBGRUPO).map((o) => ({ label: `${o.valAtr} - ${o.descrpVal}`, value: o.valAtr })),
    [atributos]
  );

  /*
        Disparador que se ejecuta ante la renderización del componente. El mismo se encarga de actualizar los productos y sus atributos disponibles del context.
    */
  useEffect(() => {
    const fn = async () => {
      await Promise.all([actualizarProducts(), actualizarAtributos()]);
      setLoading(false);
    };
    fn();
  }, []);

  /*
        Arma la lista de productos con sus atributos (tipo, grupo, subgrupo, marca) anidados, a
        partir de lo recuperado del context.
        Se agrupan los atributos por producto en un Map antes de recorrer los productos (en vez de
        filtrar el array completo de atributos por cada producto), para no volver esto O(productos x atributos).
        No se descartan acá los atributos con debaja='S': la fila del producto necesita saber si
        existían atributos y están dados de baja, para poder distinguir "sin atributos cargados" de
        "atributos inexistentes o no disponibles en ERP" (ver RRProducts.js).
    */
  useEffect(() => {
    const atributosPorProducto = new Map();
    atributos.forEach((a) => {
      const key = `${a.tipPro}-${a.artCod}`;
      if (!atributosPorProducto.has(key)) atributosPorProducto.set(key, []);
      atributosPorProducto.get(key).push(a);
    });

    const newProductos = products.map((p) => ({
      ...p,
      atributos: atributosPorProducto.get(`${p.tipPro}-${p.artCod}`) || [],
    }));
    setProductosConAtributos(newProductos);
  }, [products, atributos]);

  /*
        Manejador que se encarga de la actualización de productos que sobreescribe los productos actuales por los recuperados de Softland.
    */
  const handlerActualizarProductos = async () => {
    Swal.fire({
      title: 'Desea actualizar la lista de productos?',
      text: 'Se traeran los datos de Softland para sobreescribir la base de datos de la aplicación.',
      showCancelButton: true,
      confirmButtonText: 'Actualizar',
      cancelButtonText: 'No, cancelar',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        return await actualizarProductsDB()
          .then(response => {
            if (!response.status) {
              throw new Error(response.msg);
            }
            return response;
          })
          .catch(error => {
            Swal.showValidationMessage(`Request failed: ${error}`);
          });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(result => {
      if (result.isConfirmed) {
        Swal.fire(
          'Actualizado!',
          'La base de datos ha sido actualizada correctamente.',
          'success'
        );
      }
    });
  };

  return (
    <>
      <div>
      <div className="container-top-toolbar">
        <SelectList
          text="Tipo"
          options={opcionesTipo}
          valueAux={filtroTipo}
          handleChange={setFiltroTipo}
          isClearable
          border
        />
        <SelectList
          text="Grupo"
          options={opcionesGrupo}
          valueAux={filtroGrupo}
          handleChange={setFiltroGrupo}
          isClearable
          border
        />
        <SelectList
          text="Subgrupo"
          options={opcionesSubgrupo}
          valueAux={filtroSubgrupo}
          handleChange={setFiltroSubgrupo}
          isClearable
          border
        />
        {usuario.userType.some(item => VER_PRODUCTOS.includes(item.typeName)) && (
          <button
            className="actualizar-productos-btn"
            onClick={handlerActualizarProductos}
          >
            Actualizar Productos
          </button>
        )}
      </div>
        {loading ? (
          <div className="product-spinner-wrapper">
            <Spinner />
          </div>
        ) : (
          <ProductList
            productos={productosConAtributos}
            filtroTipo={filtroTipo?.value}
            filtroGrupo={filtroGrupo?.value}
            filtroSubgrupo={filtroSubgrupo?.value}
            actualizarProductDBIndividual={actualizarProductDBIndividual}
            ocultarPrecio={ocultarPrecio}
          />
        )}
      </div>
</>
  );
}

export default Product;